// src/pages/distribuicao/FichasTransporte.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// UI base JFB
import PageHeader from "pages/components/PageHeader";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Card,
  CircularProgress,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// =====================================================
// react-table column filter (padrão underline)
// =====================================================
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// =====================================================
// Helpers
// =====================================================
function nullIfEmpty(s) {
  const v = (s ?? "").toString().trim();
  return v === "" ? null : v;
}

function norm01(v) {
  return v === 1 ? 1 : 0;
}

function normTri(v) {
  const n = Number(v);
  return n === 0 || n === 1 || n === 2 ? n : 0;
}

function triLabel(v) {
  const n = Number(v);
  if (n === 0) return "PENDENTE";
  if (n === 1) return "CORRETA";
  if (n === 2) return "INCORRETA";
  return "PENDENTE";
}

function toInputDate(d) {
  const pad = (x) => x.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatRefTransporteOnBlur(v) {
  const raw = (v ?? "").toString().trim();
  if (raw === "") return "";

  if (/^\d+$/.test(raw) && raw.length <= 5) return `30000${raw}`;
  return raw;
}

function formatMatriculaOnBlur(v) {
  const raw = (v ?? "").toString().trim().toUpperCase();
  if (raw === "") return "";

  const clean = raw.replace(/[^A-Z0-9]/g, "");
  if (clean.length === 6) {
    return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4, 6)}`;
  }
  return raw;
}

function matriculaCleanLength(v) {
  const raw = (v ?? "").toString().trim().toUpperCase();
  const clean = raw.replace(/[^A-Z0-9]/g, "");
  return clean.length;
}

function isBusinessDay(d) {
  const day = d.getDay(); // 0 dom, 6 sáb
  return day !== 0 && day !== 6;
}

function minDateNBusinessDaysBack(n) {
  let d = new Date();
  d.setHours(0, 0, 0, 0);
  let count = 0;

  while (count < n) {
    d.setDate(d.getDate() - 1);
    if (isBusinessDay(d)) count++;
  }
  return d;
}

function podeAlterarAuxilio(dataFichaStr, isAdminLike, isArmazem) {
  if (isAdminLike) return true;
  if (!isArmazem) return false;

  const df = new Date(dataFichaStr);
  df.setHours(0, 0, 0, 0);

  const min = minDateNBusinessDaysBack(2);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return df >= min && df <= today;
}

// =====================================================
// Component
// =====================================================
export default function FichasTransporte() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const idutilizador = userData?.id || 0;

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const isArmazem = temFuncao("Armazém");
  const podeCriar = isAdminLike || isArmazem;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // filtros de pesquisa (defaults últimos 30 dias)
  const [filtro, setFiltro] = useState(() => {
    const hoje = new Date();
    const ini = new Date();
    ini.setDate(hoje.getDate() - 30);

    return {
      dataIni: toInputDate(ini),
      dataFim: toInputDate(hoje),
      reftransporte: "",
      matricula: "",
      pessoa: "",
    };
  });

  // combos (pessoas)
  const [pessoas, setPessoas] = useState([]);

  // dialog
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(false);

  const [openInfo, setOpenInfo] = useState(false);
  const [infoRow, setInfoRow] = useState(null);

  const handleOpenInfo = (row) => {
    setInfoRow(row);
    setOpenInfo(true);
  };

  const handleCloseInfo = () => {
    setOpenInfo(false);
    setInfoRow(null);
  };

  const [form, setForm] = useState(() => ({
    id: 0,
    dataficha: toInputDate(new Date()),
    reftransporte: "",
    motorista: "",
    ajudante: "",
    matricula: "",
    observacoes: "",

    // estado ficha (0 aberta, 1 encerrada)
    estado: 0,

    // mapa de carga (0 pendente, 1 correta, 2 incorreta)
    conf_mapa_carga: 0,
    conf_mapa_carga_desc: "",

    // campos “read-only” para mostrar no detalhe / futura UI
    criacao_nome: "",
    conf_mapa_carga_nome: "",
    dif_mercadoria_nome: "",
    quebras_danificado_nome: "",
    dif_vasilhame_nome: "",
    encerramento_nome: "",
    reabertura_nome: "",

    auxilio_mot_arm1: 0,
    auxilio_mot_arm2: 0,
    auxilio_ajud_arm1: 0,
    auxilio_ajud_arm2: 0,

    dif_mercadoria: 0,
    dif_mercadoria_desc: "",
    quebras_danificado: 0,
    quebras_danificado_desc: "",
    dif_vasilhame: 0,
    dif_vasilhame_desc: "",
  }));

  // =====================================================
  // Columns (com filtros underline)
  // =====================================================
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => {
          const fichaEncerrada = row.original?.estado === 1;

          // aberta: AdminLike/Armazém (quem pode criar) | encerrada: só AdminLike
          const podeEditarEstaFicha = fichaEncerrada ? isAdminLike : podeCriar;

          return (
            <>
              {podeCriar && (
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(row.original)}
                  title={
                    fichaEncerrada
                      ? isAdminLike
                        ? "Editar (ficha encerrada)"
                        : "Só AdminLike pode editar fichas encerradas"
                      : "Editar (ficha aberta)"
                  }
                  disabled={!podeEditarEstaFicha}
                >
                  <EditIcon color="error" fontSize="small" />
                </IconButton>
              )}

              <IconButton size="small" onClick={() => handleOpenInfo(row.original)} title="Info">
                <InfoOutlinedIcon color="info" fontSize="small" />
              </IconButton>
            </>
          );
        },
      },

      {
        Header: "Data",
        accessor: "dataficha",
        Filter: TextFilter,
        Cell: ({ value }) => formatDateOnly(value),
      },

      {
        Header: "Ref. Transp.",
        accessor: "reftransporte",
        Filter: TextFilter,
        Cell: ({ value }) => (
          <span
            onClick={() => abrirTransportesRef(value)}
            title="Ver transportes desta referência"
            style={{
              color: "#1a73e8",
              textDecoration: "underline",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {value}
          </span>
        ),
      },

      { Header: "Motorista", accessor: "motorista", Filter: TextFilter },
      { Header: "Ajudante", accessor: "ajudante", Filter: TextFilter },
      { Header: "Matrícula", accessor: "matricula", Filter: TextFilter },

      {
        Header: "Auxílio Motorista Arm.1",
        accessor: "auxilio_mot_arm1",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },
      {
        Header: "Auxílio Ajudante Arm.1",
        accessor: "auxilio_ajud_arm1",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },
      {
        Header: "Auxílio Motorista Arm.2",
        accessor: "auxilio_mot_arm2",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },

      {
        Header: "Auxílio Ajudante Arm.2",
        accessor: "auxilio_ajud_arm2",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },
      {
        Header: "Mapa Carga",
        accessor: "conf_mapa_carga",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Dif. Mercad.",
        accessor: "dif_mercadoria",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Quebras/Danif.",
        accessor: "quebras_danificado",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Dif. Vasil.",
        accessor: "dif_vasilhame",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
    ],
    []
  );

  const data = useMemo(() => rows, [rows]);

  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: TextFilter },
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = tableInstance;

  // =====================================================
  // Load combos (pessoas)
  // =====================================================
  const carregarPessoas = async () => {
    if (!idutilizador) return;

    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelTransportesCombos", { idutilizador })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    const lista = dados?.pessoas || [];
    setPessoas(Array.isArray(lista) ? lista : []);
  };

  // =====================================================
  // Pesquisa
  // =====================================================
  const pesquisar = async () => {
    if (!idutilizador) return;

    setLoading(true);

    const pars = {
      dataIni: filtro.dataIni,
      dataFim: filtro.dataFim,
      reftransporte: nullIfEmpty(filtro.reftransporte),
      matricula: nullIfEmpty(filtro.matricula),
      pessoa: nullIfEmpty(filtro.pessoa),
      idutilizador,
    };

    const [dados, err, meta] = await handleApi(api.post("Distribuicao/SelFichasTransporte", pars));

    if (err) {
      showAlert(err, "error");
      setLoading(false);
      return;
    }

    setRows(Array.isArray(dados) ? dados : []);
    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    setLoading(false);
  };

  useEffect(() => {
    if (idutilizador > 0) {
      (async () => {
        await carregarPessoas();
        await pesquisar();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idutilizador]);

  // =====================================================
  // Dialog open/close
  // =====================================================
  const resetFormNovo = () => {
    setForm({
      id: 0,
      dataficha: toInputDate(new Date()),
      reftransporte: "",
      motorista: "",
      ajudante: "",
      matricula: "",
      observacoes: "",

      // estado ficha (0 aberta, 1 encerrada)
      estado: 0,

      // mapa de carga (0 pendente, 1 correta, 2 incorreta)
      conf_mapa_carga: 0,
      conf_mapa_carga_desc: "",

      // campos “read-only” para mostrar no detalhe / futura UI
      criacao_nome: "",
      conf_mapa_carga_nome: "",
      dif_mercadoria_nome: "",
      quebras_danificado_nome: "",
      dif_vasilhame_nome: "",
      encerramento_nome: "",
      reabertura_nome: "",

      auxilio_mot_arm1: 0,
      auxilio_mot_arm2: 0,
      auxilio_ajud_arm1: 0,
      auxilio_ajud_arm2: 0,

      dif_mercadoria: 0,
      dif_mercadoria_desc: "",
      quebras_danificado: 0,
      quebras_danificado_desc: "",
      dif_vasilhame: 0,
      dif_vasilhame_desc: "",
    });
  };

  const handleOpenDialogNovo = () => {
    setEditando(false);
    resetFormNovo();
    setOpen(true);
  };

  const handleOpenDialog = (row) => {
    setEditando(true);
    setForm({
      id: row.id,
      dataficha: row.dataficha?.slice?.(0, 10) || row.dataficha || toInputDate(new Date()),
      reftransporte: row.reftransporte || "",
      motorista: row.motorista || "",
      ajudante: row.ajudante || "",
      matricula: row.matricula || "",
      observacoes: row.observacoes || "",

      estado: row.estado ?? 0,

      conf_mapa_carga: row.conf_mapa_carga ?? 0,
      conf_mapa_carga_desc: row.conf_mapa_carga_desc || "",

      criacao_nome: row.criacao_nome || "",
      conf_mapa_carga_nome: row.conf_mapa_carga_nome || "",
      dif_mercadoria_nome: row.dif_mercadoria_nome || "",
      quebras_danificado_nome: row.quebras_danificado_nome || "",
      dif_vasilhame_nome: row.dif_vasilhame_nome || "",
      encerramento_nome: row.encerramento_nome || "",
      reabertura_nome: row.reabertura_nome || "",

      auxilio_mot_arm1: row.auxilio_mot_arm1 ?? 0,
      auxilio_mot_arm2: row.auxilio_mot_arm2 ?? 0,
      auxilio_ajud_arm1: row.auxilio_ajud_arm1 ?? 0,
      auxilio_ajud_arm2: row.auxilio_ajud_arm2 ?? 0,

      dif_mercadoria: row.dif_mercadoria ?? 0,
      dif_mercadoria_desc: row.dif_mercadoria_desc || "",
      quebras_danificado: row.quebras_danificado ?? 0,
      quebras_danificado_desc: row.quebras_danificado_desc || "",
      dif_vasilhame: row.dif_vasilhame ?? 0,
      dif_vasilhame_desc: row.dif_vasilhame_desc || "",
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // =====================================================
  // Save
  // =====================================================
  const validar = () => {
    if (nullIfEmpty(form.dataficha) == null) return "Data da Ficha é obrigatória.";
    if (nullIfEmpty(form.reftransporte) == null) return "Ref. Transporte é obrigatória.";
    if (nullIfEmpty(form.motorista) == null) return "Motorista é obrigatório.";

    const lenMat = matriculaCleanLength(form.matricula);
    if (lenMat > 0 && lenMat < 6)
      return "A matrícula tem de ter pelo menos 6 caracteres (sem contar hífens).";

    // Mapa de carga tem de ser CORRETA ou INCORRETA
    if (normTri(form.conf_mapa_carga) === 0)
      return "Mapa de Carga é obrigatório (CORRETA ou INCORRETA).";

    if (normTri(form.conf_mapa_carga) === 2 && nullIfEmpty(form.conf_mapa_carga_desc) == null)
      return "Descritivo do Mapa de Carga é obrigatório.";

    if (normTri(form.dif_mercadoria) === 2 && nullIfEmpty(form.dif_mercadoria_desc) == null)
      return "Descritivo de Diferença na Mercadoria é obrigatório.";

    if (normTri(form.quebras_danificado) === 2 && nullIfEmpty(form.quebras_danificado_desc) == null)
      return "Descritivo de Quebras / Produto Danificado é obrigatório.";

    if (normTri(form.dif_vasilhame) === 2 && nullIfEmpty(form.dif_vasilhame_desc) == null)
      return "Descritivo de Diferença no Vasilhame é obrigatório.";

    return null;
  };

  const handleSave = async () => {
    const erro = validar();
    if (erro) {
      showAlert(erro, "error");
      return;
    }

    const parsBase = {
      dataficha: form.dataficha,
      reftransporte: nullIfEmpty(form.reftransporte),

      motorista: nullIfEmpty(form.motorista),
      ajudante: nullIfEmpty(form.ajudante),

      matricula: nullIfEmpty(form.matricula),
      observacoes: nullIfEmpty(form.observacoes),
      estado: Number(form.estado ?? 0),

      auxilio_mot_arm1: norm01(form.auxilio_mot_arm1),
      auxilio_mot_arm2: norm01(form.auxilio_mot_arm2),
      auxilio_ajud_arm1: norm01(form.auxilio_ajud_arm1),
      auxilio_ajud_arm2: norm01(form.auxilio_ajud_arm2),

      conf_mapa_carga: normTri(form.conf_mapa_carga),
      conf_mapa_carga_desc: nullIfEmpty(form.conf_mapa_carga_desc),

      dif_mercadoria: normTri(form.dif_mercadoria),
      dif_mercadoria_desc: nullIfEmpty(form.dif_mercadoria_desc),

      quebras_danificado: normTri(form.quebras_danificado),
      quebras_danificado_desc: nullIfEmpty(form.quebras_danificado_desc),

      dif_vasilhame: normTri(form.dif_vasilhame),
      dif_vasilhame_desc: nullIfEmpty(form.dif_vasilhame_desc),

      idutilizador,
    };

    const [dados, err, meta] = await handleApi(
      !editando
        ? api.post("Distribuicao/InsFichaTransporte", parsBase)
        : api.post("Distribuicao/UpdFichaTransporte", { ...parsBase, id: form.id })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    // Se for criação: ficar no dialog, receber o ID e desbloquear as secções abaixo
    if (!editando) {
      const newId = Number(dados?.id || 0);

      if (!newId) {
        showAlert("A ficha foi criada mas não recebi o ID. Verifica a resposta da API.", "error");
        return;
      }

      setForm((s) => ({ ...s, id: newId }));
      setEditando(true);

      // Atualiza lista, mas não fecha o dialog
      await pesquisar();
      return;
    }

    // Edição normal: fecha e refresca
    handleClose();
    await pesquisar();
  };

  // =====================================================
  // UI helpers (sem duplicar pessoas)
  // =====================================================

  const abrirTransportesRef = (ref) => {
    const r = (ref ?? "").toString().trim();
    if (!r) return;

    // usa o mesmo route que tens no menu "Transportes"
    const url = `/distribuicao/transportes?reftransporte=${encodeURIComponent(r)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  function formatDateTime(v) {
    if (!v) return "";
    const s = String(v);
    // "2026-02-06T12:34:56Z" ou "...+00"
    if (s.includes("T")) return s.replace("T", " ").slice(0, 19);
    return s;
  }

  function quemQuando(nome, dt) {
    const n = (nome ?? "").toString().trim();
    const d = formatDateTime(dt);
    if (!n && !d) return "—";
    if (n && d) return `${n} em ${d}`;
    return n || d;
  }

  function formatDateOnly(v) {
    if (!v) return "";

    // Se já vier como Date
    if (v instanceof Date) {
      return v.toISOString().slice(0, 10);
    }

    const s = String(v);

    // "2026-02-06T00:00:00"
    if (s.includes("T")) return s.split("T")[0];

    // "06/02/2026 00:00:00" (ou semelhante)
    if (s.includes(" ")) return s.split(" ")[0];

    return s;
  }

  const pessoasMotorista = useMemo(() => {
    const ajud = nullIfEmpty(form.ajudante);
    return pessoas.filter((p) => (p ?? "") !== ajud);
  }, [pessoas, form.ajudante]);

  const pessoasAjudante = useMemo(() => {
    const mot = nullIfEmpty(form.motorista);
    return pessoas.filter((p) => (p ?? "") !== mot);
  }, [pessoas, form.motorista]);

  const podeEditarAux = podeAlterarAuxilio(form.dataficha, isAdminLike, isArmazem);

  const temId = Number(form.id) > 0;

  const temPendentesOcorrencias =
    normTri(form.dif_mercadoria) === 0 ||
    normTri(form.quebras_danificado) === 0 ||
    normTri(form.dif_vasilhame) === 0;

  const handleEncerrar = async () => {
    if (!temId) return;

    if (temPendentesOcorrencias) {
      showAlert("Não é possível encerrar: existem ocorrências em PENDENTE.", "error");
      return;
    }

    const parsBase = {
      dataficha: form.dataficha,
      reftransporte: nullIfEmpty(form.reftransporte),

      motorista: nullIfEmpty(form.motorista),
      ajudante: nullIfEmpty(form.ajudante),

      matricula: nullIfEmpty(form.matricula),
      observacoes: nullIfEmpty(form.observacoes),

      auxilio_mot_arm1: norm01(form.auxilio_mot_arm1),
      auxilio_mot_arm2: norm01(form.auxilio_mot_arm2),
      auxilio_ajud_arm1: norm01(form.auxilio_ajud_arm1),
      auxilio_ajud_arm2: norm01(form.auxilio_ajud_arm2),

      conf_mapa_carga: normTri(form.conf_mapa_carga),
      conf_mapa_carga_desc: nullIfEmpty(form.conf_mapa_carga_desc),

      dif_mercadoria: normTri(form.dif_mercadoria),
      dif_mercadoria_desc: nullIfEmpty(form.dif_mercadoria_desc),

      quebras_danificado: normTri(form.quebras_danificado),
      quebras_danificado_desc: nullIfEmpty(form.quebras_danificado_desc),

      dif_vasilhame: normTri(form.dif_vasilhame),
      dif_vasilhame_desc: nullIfEmpty(form.dif_vasilhame_desc),

      estado: 1,
      idutilizador,
    };

    const [_, err, meta] = await handleApi(
      api.post("Distribuicao/UpdFichaTransporte", { ...parsBase, id: form.id })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    setForm((s) => ({ ...s, estado: 1 }));
    handleClose();
    await pesquisar();
  };

  const handleReabrir = async () => {
    if (!temId) return;
    if (!isAdminLike) return;

    const parsBase = {
      dataficha: form.dataficha,
      reftransporte: nullIfEmpty(form.reftransporte),

      motorista: nullIfEmpty(form.motorista),
      ajudante: nullIfEmpty(form.ajudante),

      matricula: nullIfEmpty(form.matricula),
      observacoes: nullIfEmpty(form.observacoes),

      auxilio_mot_arm1: norm01(form.auxilio_mot_arm1),
      auxilio_mot_arm2: norm01(form.auxilio_mot_arm2),
      auxilio_ajud_arm1: norm01(form.auxilio_ajud_arm1),
      auxilio_ajud_arm2: norm01(form.auxilio_ajud_arm2),

      conf_mapa_carga: normTri(form.conf_mapa_carga),
      conf_mapa_carga_desc: nullIfEmpty(form.conf_mapa_carga_desc),

      dif_mercadoria: normTri(form.dif_mercadoria),
      dif_mercadoria_desc: nullIfEmpty(form.dif_mercadoria_desc),

      quebras_danificado: normTri(form.quebras_danificado),
      quebras_danificado_desc: nullIfEmpty(form.quebras_danificado_desc),

      dif_vasilhame: normTri(form.dif_vasilhame),
      dif_vasilhame_desc: nullIfEmpty(form.dif_vasilhame_desc),

      estado: 0,
      idutilizador,
    };

    const [_, err, meta] = await handleApi(
      api.post("Distribuicao/UpdFichaTransporte", { ...parsBase, id: form.id })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    setForm((s) => ({ ...s, estado: 0 }));
    await pesquisar();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          {/* Linha de filtros (layout JFB) */}
          <MDBox p={2} display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data inicial
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={filtro.dataIni}
                onChange={(e) => setFiltro((s) => ({ ...s, dataIni: e.target.value }))}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data final
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={filtro.dataFim}
                onChange={(e) => setFiltro((s) => ({ ...s, dataFim: e.target.value }))}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 180 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Transporte (ref)
              </MDTypography>
              <TextField
                size="small"
                value={filtro.reftransporte}
                onChange={(e) => setFiltro((s) => ({ ...s, reftransporte: e.target.value }))}
                onBlur={(e) =>
                  setFiltro((s) => ({
                    ...s,
                    reftransporte: formatRefTransporteOnBlur(e.target.value),
                  }))
                }
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Matrícula
              </MDTypography>
              <TextField
                size="small"
                value={filtro.matricula}
                onChange={(e) =>
                  setFiltro((s) => ({
                    ...s,
                    matricula: (e.target.value || "").toUpperCase(),
                  }))
                }
                onBlur={(e) => {
                  const val = e.target.value;
                  const len = matriculaCleanLength(val);

                  if (len > 0 && len < 6) {
                    showAlert(
                      "A matrícula tem de ter pelo menos 6 caracteres (sem contar hífens).",
                      "error"
                    );
                    return;
                  }

                  setFiltro((s) => ({
                    ...s,
                    matricula: formatMatriculaOnBlur(val),
                  }));
                }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 260 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Motoristas / Ajudantes
              </MDTypography>
              <Autocomplete
                size="small"
                options={pessoas}
                value={filtro.pessoa || null}
                onChange={(_, v) => setFiltro((s) => ({ ...s, pessoa: v || "" }))}
                getOptionLabel={(opt) => opt || ""}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODOS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            <MDBox display="flex" alignItems="flex-end" gap={1}>
              <MDButton color="info" onClick={pesquisar} disabled={loading}>
                ATUALIZAR ANÁLISE
              </MDButton>

              {podeCriar && (
                <MDButton color="success" onClick={handleOpenDialogNovo}>
                  NOVA FICHA
                </MDButton>
              )}
            </MDBox>
          </MDBox>

          {/* Registos */}
          <MDBox px={2} pb={1}>
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Registos: {rows.length}
            </MDTypography>
          </MDBox>

          {/* Tabela */}
          <MDBox px={2} pb={2} style={{ overflowX: "auto" }}>
            <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {headerGroups.map((hg) => (
                  <tr key={hg.id} {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th
                        key={col.id}
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        style={{
                          textAlign: "left",
                          padding: "10px 10px 6px 10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          verticalAlign: "bottom",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.75}>
                          <span>{col.render("Header")}</span>
                          <span style={{ fontSize: 11, opacity: 0.7 }}>
                            {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                          </span>
                        </MDBox>

                        <MDBox mt={0.5}>{col.canFilter ? col.render("Filter") : null}</MDBox>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {tableRows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr key={row.id} {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MDBox>
        </Card>
      </MDBox>

      {/* DIALOG */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editando ? "Editar Ficha de Transporte" : "Nova Ficha de Transporte"}
        </DialogTitle>

        <DialogContent dividers>
          <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data da Ficha
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={form.dataficha}
                onChange={(e) => setForm((s) => ({ ...s, dataficha: e.target.value }))}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Ref. Transporte *
              </MDTypography>
              <TextField
                size="small"
                value={form.reftransporte}
                onChange={(e) => setForm((s) => ({ ...s, reftransporte: e.target.value }))}
                onBlur={(e) =>
                  setForm((s) => ({
                    ...s,
                    reftransporte: formatRefTransporteOnBlur(e.target.value),
                  }))
                }
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Matrícula
              </MDTypography>
              <TextField
                size="small"
                value={form.matricula}
                onChange={(e) =>
                  setForm((s) => ({ ...s, matricula: (e.target.value || "").toUpperCase() }))
                }
                onBlur={(e) => {
                  const val = e.target.value;
                  const len = matriculaCleanLength(val);

                  if (len > 0 && len < 6) {
                    showAlert(
                      "A matrícula tem de ter pelo menos 6 caracteres (sem contar hífens).",
                      "error"
                    );
                    return;
                  }

                  setForm((s) => ({ ...s, matricula: formatMatriculaOnBlur(val) }));
                }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 240 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Mapa de Carga *
              </MDTypography>

              <select
                value={normTri(form.conf_mapa_carga)}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    conf_mapa_carga: normTri(e.target.value),
                    conf_mapa_carga_desc:
                      normTri(e.target.value) === 2 ? s.conf_mapa_carga_desc : "",
                  }))
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.23)",
                  fontSize: 13,
                  background: "white",
                }}
              >
                <option value={0}>PENDENTE</option>
                <option value={1}>CORRETA</option>
                <option value={2}>INCORRETA</option>
              </select>

              {normTri(form.conf_mapa_carga) === 2 && (
                <TextField
                  size="small"
                  multiline
                  rows={3}
                  value={form.conf_mapa_carga_desc}
                  onChange={(e) => setForm((s) => ({ ...s, conf_mapa_carga_desc: e.target.value }))}
                  placeholder="Descritivo (obrigatório)"
                  fullWidth
                  sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                />
              )}
            </MDBox>

            <MDBox sx={{ minWidth: 260 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Motorista
              </MDTypography>
              <Autocomplete
                size="small"
                options={pessoasMotorista}
                value={nullIfEmpty(form.motorista) || null}
                onChange={(_, v) => {
                  const val = v || "";
                  setForm((s) => ({
                    ...s,
                    motorista: val,
                    auxilio_mot_arm1: val ? s.auxilio_mot_arm1 : 0,
                    auxilio_mot_arm2: val ? s.auxilio_mot_arm2 : 0,
                  }));
                }}
                getOptionLabel={(opt) => opt || ""}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="(nenhum)"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 260 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Ajudante
              </MDTypography>
              <Autocomplete
                size="small"
                options={pessoasAjudante}
                value={nullIfEmpty(form.ajudante) || null}
                onChange={(_, v) => {
                  const val = v || "";
                  setForm((s) => ({
                    ...s,
                    ajudante: val,
                    auxilio_ajud_arm1: val ? s.auxilio_ajud_arm1 : 0,
                    auxilio_ajud_arm2: val ? s.auxilio_ajud_arm2 : 0,
                  }));
                }}
                getOptionLabel={(opt) => opt || ""}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="(nenhum)"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            {temId ? (
              <MDBox sx={{ flex: 1, minWidth: 320 }}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                  Observações
                </MDTypography>
                <TextField
                  size="small"
                  multiline
                  rows={3}
                  value={form.observacoes}
                  onChange={(e) => setForm((s) => ({ ...s, observacoes: e.target.value }))}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                />
              </MDBox>
            ) : (
              <MDBox sx={{ flex: 1, minWidth: 320 }}>
                <MDTypography variant="button" sx={{ fontSize: 12, opacity: 0.75 }}>
                  Após gravar (para obter o ID), ficam disponíveis: Observações, Auxílios e
                  Ocorrências.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>

          {temId && (
            <MDBox>
              {/* AUXÍLIOS */}
              <MDBox mt={3}>
                <MDTypography variant="button" sx={{ fontSize: 13, fontWeight: 700 }}>
                  Auxílio Armazém
                </MDTypography>

                {nullIfEmpty(form.motorista) && (
                  <MDBox mt={1} display="flex" gap={2} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.auxilio_mot_arm1 === 1}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, auxilio_mot_arm1: e.target.checked ? 1 : 0 }))
                          }
                          disabled={!podeEditarAux}
                        />
                      }
                      label="Motorista auxiliou no Armazém 1"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.auxilio_mot_arm2 === 1}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, auxilio_mot_arm2: e.target.checked ? 1 : 0 }))
                          }
                          disabled={!podeEditarAux}
                        />
                      }
                      label="Motorista auxiliou no Armazém 2"
                    />
                  </MDBox>
                )}

                {nullIfEmpty(form.ajudante) && (
                  <MDBox mt={1} display="flex" gap={2} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.auxilio_ajud_arm1 === 1}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, auxilio_ajud_arm1: e.target.checked ? 1 : 0 }))
                          }
                          disabled={!podeEditarAux}
                        />
                      }
                      label="Ajudante auxiliou no Armazém 1"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.auxilio_ajud_arm2 === 1}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, auxilio_ajud_arm2: e.target.checked ? 1 : 0 }))
                          }
                          disabled={!podeEditarAux}
                        />
                      }
                      label="Ajudante auxiliou no Armazém 2"
                    />
                  </MDBox>
                )}

                {(nullIfEmpty(form.motorista) || nullIfEmpty(form.ajudante)) && !podeEditarAux && (
                  <MDBox mt={1}>
                    <MDTypography variant="button" sx={{ fontSize: 12, opacity: 0.8 }}>
                      Nota: Auxílios só podem ser alterados por AdminLike, ou Armazém no dia / até 2
                      dias úteis anteriores.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>

              {/* OCORRÊNCIAS */}
              <MDBox mt={3}>
                <MDTypography variant="button" sx={{ fontSize: 13, fontWeight: 700 }}>
                  Ocorrências
                </MDTypography>

                <MDBox mt={1} display="flex" flexDirection="column" gap={2}>
                  {/* Diferença na Mercadoria */}
                  <MDBox>
                    <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                      Diferença na Mercadoria
                    </MDTypography>

                    <select
                      value={normTri(form.dif_mercadoria)}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          dif_mercadoria: normTri(e.target.value),
                          dif_mercadoria_desc:
                            normTri(e.target.value) === 2 ? s.dif_mercadoria_desc : "",
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.23)",
                        fontSize: 13,
                        background: "white",
                      }}
                    >
                      <option value={0}>PENDENTE</option>
                      <option value={1}>CORRETA</option>
                      <option value={2}>INCORRETA</option>
                    </select>

                    {normTri(form.dif_mercadoria) === 2 && (
                      <TextField
                        size="small"
                        multiline
                        rows={3}
                        value={form.dif_mercadoria_desc}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, dif_mercadoria_desc: e.target.value }))
                        }
                        placeholder="Descritivo (obrigatório)"
                        fullWidth
                        sx={{
                          mt: 1,
                          "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                        }}
                      />
                    )}
                  </MDBox>

                  {/* Quebras / Produto Danificado */}
                  <MDBox>
                    <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                      Quebras ou Produto Danificado
                    </MDTypography>

                    <select
                      value={normTri(form.quebras_danificado)}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          quebras_danificado: normTri(e.target.value),
                          quebras_danificado_desc:
                            normTri(e.target.value) === 2 ? s.quebras_danificado_desc : "",
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.23)",
                        fontSize: 13,
                        background: "white",
                      }}
                    >
                      <option value={0}>PENDENTE</option>
                      <option value={1}>CORRETA</option>
                      <option value={2}>INCORRETA</option>
                    </select>

                    {normTri(form.quebras_danificado) === 2 && (
                      <TextField
                        size="small"
                        multiline
                        rows={3}
                        value={form.quebras_danificado_desc}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, quebras_danificado_desc: e.target.value }))
                        }
                        placeholder="Descritivo (obrigatório)"
                        fullWidth
                        sx={{
                          mt: 1,
                          "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                        }}
                      />
                    )}
                  </MDBox>

                  {/* Diferença no Vasilhame */}
                  <MDBox>
                    <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                      Diferença no Vasilhame
                    </MDTypography>

                    <select
                      value={normTri(form.dif_vasilhame)}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          dif_vasilhame: normTri(e.target.value),
                          dif_vasilhame_desc:
                            normTri(e.target.value) === 2 ? s.dif_vasilhame_desc : "",
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.23)",
                        fontSize: 13,
                        background: "white",
                      }}
                    >
                      <option value={0}>PENDENTE</option>
                      <option value={1}>CORRETA</option>
                      <option value={2}>INCORRETA</option>
                    </select>

                    {normTri(form.dif_vasilhame) === 2 && (
                      <TextField
                        size="small"
                        multiline
                        rows={3}
                        value={form.dif_vasilhame_desc}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, dif_vasilhame_desc: e.target.value }))
                        }
                        placeholder="Descritivo (obrigatório)"
                        fullWidth
                        sx={{
                          mt: 1,
                          "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                        }}
                      />
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="light" onClick={handleClose}>
            Cancelar
          </MDButton>

          {temId && form.estado === 0 && (
            <MDButton color="success" onClick={handleEncerrar} disabled={temPendentesOcorrencias}>
              Encerrar
            </MDButton>
          )}

          {temId && form.estado === 1 && isAdminLike && (
            <MDButton color="warning" onClick={handleReabrir}>
              Reabrir
            </MDButton>
          )}

          <MDButton
            color="info"
            onClick={handleSave}
            disabled={normTri(form.conf_mapa_carga) === 0}
          >
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openInfo} onClose={handleCloseInfo} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalhe da Ficha de Transporte Refª
          <MDTypography
            component="span"
            sx={{
              color: "info.main",
              cursor: "pointer",
              // textDecoration: "underline",
              ml: 1,
            }}
            onClick={() => infoRow?.reftransporte && abrirTransportesRef(infoRow.reftransporte)}
          >
            {infoRow?.reftransporte}
          </MDTypography>
        </DialogTitle>

        <DialogContent dividers>
          {/* TOPO: Geral + Auxílio */}
          <MDBox display="flex" gap={2} flexWrap="wrap">
            {/* GERAL */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 300,
                p: 2,
                borderRadius: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
              }}
            >
              <MDTypography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Geral</MDTypography>

              <MDTypography sx={{ fontSize: 13, mb: 0.5 }}>
                <b>Data:</b> {infoRow?.dataficha ? String(infoRow.dataficha).slice(0, 10) : ""}
              </MDTypography>

              <MDTypography sx={{ fontSize: 13, mb: 0.5 }}>
                <b>Motorista:</b> {infoRow?.motorista || "—"}
              </MDTypography>

              <MDTypography sx={{ fontSize: 13, mb: 0.5 }}>
                <b>Ajudante:</b> {infoRow?.ajudante || "—"}
              </MDTypography>

              <MDTypography sx={{ fontSize: 13, mb: 1 }}>
                <b>Matrícula:</b> {infoRow?.matricula || "—"}
              </MDTypography>

              <MDTypography sx={{ fontSize: 13, mb: 0.5 }}>
                <b>Mapa de Carga:</b> {triLabel(infoRow?.conf_mapa_carga)}
              </MDTypography>

              {normTri(infoRow?.conf_mapa_carga) === 2 && (
                <MDTypography sx={{ fontSize: 12, mb: 0.5, opacity: 0.85, whiteSpace: "pre-wrap" }}>
                  {infoRow?.conf_mapa_carga_desc || "(sem descritivo)"}
                </MDTypography>
              )}

              <MDTypography sx={{ fontSize: 12, mb: 1, opacity: 0.75 }}>
                <b>Atualizado por:</b>{" "}
                {quemQuando(infoRow?.conf_mapa_carga_nome, infoRow?.conf_mapa_carga_datahora)}
              </MDTypography>

              <MDTypography sx={{ fontSize: 13, fontWeight: 700, mt: 1 }}>Observações</MDTypography>
              <MDBox
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "#ffffff",
                  border: "1px dashed #ccc",
                  fontSize: 13,
                  whiteSpace: "pre-wrap",
                }}
              >
                {infoRow?.observacoes || "(sem observações)"}
              </MDBox>

              <MDTypography sx={{ fontSize: 12, mt: 1, opacity: 0.8 }}>
                <b>Encerramento:</b>{" "}
                {quemQuando(infoRow?.encerramento_nome, infoRow?.dataencerramento)}
              </MDTypography>

              <MDTypography sx={{ fontSize: 12, opacity: 0.8 }}>
                <b>Reabertura:</b> {quemQuando(infoRow?.reabertura_nome, infoRow?.datareabertura)}
              </MDTypography>
            </MDBox>

            {/* AUXÍLIO ARMAZÉM */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 260,
                p: 2,
                borderRadius: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
              }}
            >
              <MDTypography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
                Auxílio Armazém
              </MDTypography>

              <MDTypography sx={{ fontSize: 13 }}>
                <b>Motorista Arm1:</b> {infoRow?.auxilio_mot_arm1 === 1 ? "Sim" : "Não"}
              </MDTypography>
              <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                <b>Atualizado por:</b>{" "}
                {quemQuando(infoRow?.auxilio_mot_arm1_nome, infoRow?.auxilio_mot_arm1_datahora)}
              </MDTypography>

              <MDTypography sx={{ fontSize: 13, mt: 1.5 }}>
                <b>Motorista Arm2:</b> {infoRow?.auxilio_mot_arm2 === 1 ? "Sim" : "Não"}
              </MDTypography>
              <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                <b>Atualizado por:</b>{" "}
                {quemQuando(infoRow?.auxilio_mot_arm2_nome, infoRow?.auxilio_mot_arm2_datahora)}
              </MDTypography>

              <MDBox mt={1}>
                <MDTypography sx={{ fontSize: 13, mt: 1.5 }}>
                  <b>Ajudante Arm1:</b> {infoRow?.auxilio_ajud_arm1 === 1 ? "Sim" : "Não"}
                </MDTypography>
                <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                  <b>Atualizado por:</b>{" "}
                  {quemQuando(infoRow?.auxilio_ajud_arm1_nome, infoRow?.auxilio_ajud_arm1_datahora)}
                </MDTypography>
                <MDTypography sx={{ fontSize: 13, mt: 1.5 }}>
                  <b>Ajudante Arm2:</b> {infoRow?.auxilio_ajud_arm2 === 1 ? "Sim" : "Não"}
                </MDTypography>
                <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                  <b>Atualizado por:</b>{" "}
                  {quemQuando(infoRow?.auxilio_ajud_arm2_nome, infoRow?.auxilio_ajud_arm2_datahora)}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* OCORRÊNCIAS (100% largura) */}
          <MDBox
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "#f8f9fa",
              border: "1px solid #e0e0e0",
            }}
          >
            <MDTypography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Ocorrências</MDTypography>

            <MDBox display="flex" gap={2} flexWrap="wrap">
              {/* Dif. Mercadoria */}
              <MDBox
                sx={{
                  flex: 1,
                  minWidth: 200,
                  p: 1.5,
                  bgcolor: "#ffffff",
                  borderRadius: 1.5,
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDTypography sx={{ fontSize: 13, fontWeight: 600 }}>Dif. Mercadoria</MDTypography>
                <MDTypography sx={{ fontSize: 13 }}>
                  {triLabel(infoRow?.dif_mercadoria)}
                </MDTypography>
                <MDTypography sx={{ fontSize: 12, mt: 0.5, whiteSpace: "pre-wrap", opacity: 0.85 }}>
                  {normTri(infoRow?.dif_mercadoria) === 2
                    ? infoRow?.dif_mercadoria_desc || "(sem descritivo)"
                    : "—"}
                </MDTypography>
                <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                  <b>Atualizado por:</b>{" "}
                  {quemQuando(infoRow?.dif_mercadoria_nome, infoRow?.dif_mercadoria_datahora)}
                </MDTypography>
              </MDBox>

              {/* Quebras / Danificado */}
              <MDBox
                sx={{
                  flex: 1,
                  minWidth: 200,
                  p: 1.5,
                  bgcolor: "#ffffff",
                  borderRadius: 1.5,
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDTypography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Quebras / Danificado
                </MDTypography>
                <MDTypography sx={{ fontSize: 13 }}>
                  {triLabel(infoRow?.quebras_danificado)}
                </MDTypography>

                <MDTypography sx={{ fontSize: 12, mt: 0.5, whiteSpace: "pre-wrap", opacity: 0.85 }}>
                  {normTri(infoRow?.quebras_danificado) === 2
                    ? infoRow?.quebras_danificado_desc || "(sem descritivo)"
                    : "—"}
                </MDTypography>

                <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                  <b>Atualizado por:</b>{" "}
                  {quemQuando(
                    infoRow?.quebras_danificado_nome,
                    infoRow?.quebras_danificado_datahora
                  )}
                </MDTypography>
              </MDBox>

              {/* Dif. Vasilhame */}
              <MDBox
                sx={{
                  flex: 1,
                  minWidth: 200,
                  p: 1.5,
                  bgcolor: "#ffffff",
                  borderRadius: 1.5,
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDTypography sx={{ fontSize: 13, fontWeight: 600 }}>Dif. Vasilhame</MDTypography>
                <MDTypography sx={{ fontSize: 13 }}>
                  {triLabel(infoRow?.dif_vasilhame)}
                </MDTypography>

                <MDTypography sx={{ fontSize: 12, mt: 0.5, whiteSpace: "pre-wrap", opacity: 0.85 }}>
                  {normTri(infoRow?.dif_vasilhame) === 2
                    ? infoRow?.dif_vasilhame_desc || "(sem descritivo)"
                    : "—"}
                </MDTypography>

                <MDTypography sx={{ fontSize: 12, mt: 0.5, opacity: 0.75 }}>
                  <b>Atualizado por:</b>{" "}
                  {quemQuando(infoRow?.dif_vasilhame_nome, infoRow?.dif_vasilhame_datahora)}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="light" onClick={handleCloseInfo}>
            Fechar
          </MDButton>
          {/* <MDButton
            color="info"
            onClick={() => infoRow?.reftransporte && abrirTransportesRef(infoRow.reftransporte)}
            disabled={!infoRow?.reftransporte}
          >
            Abrir Transportes
          </MDButton> */}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
